import { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { CloudOff, Cloud, RefreshCw, Download, Upload, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

// IndexedDB helper functions
const DB_NAME = "avx_rf_pro_offline";
const DB_VERSION = 1;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains("systems")) {
        db.createObjectStore("systems", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("events")) {
        db.createObjectStore("events", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("scans")) {
        db.createObjectStore("scans", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("pending_sync")) {
        db.createObjectStore("pending_sync", { keyPath: "sync_id" });
      }
      if (!db.objectStoreNames.contains("metadata")) {
        db.createObjectStore("metadata", { keyPath: "key" });
      }
    };
  });
};

const saveToStore = async (storeName, data) => {
  const db = await openDB();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  
  for (const item of data) {
    store.put(item);
  }
  
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const getFromStore = async (storeName) => {
  const db = await openDB();
  const tx = db.transaction(storeName, "readonly");
  const store = tx.objectStore(storeName);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const clearStore = async (storeName) => {
  const db = await openDB();
  const tx = db.transaction(storeName, "readwrite");
  const store = tx.objectStore(storeName);
  store.clear();
  
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export default function OfflineSyncPage({ user, onLogout }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState(null);
  const [offlineData, setOfflineData] = useState({
    systems: 0,
    events: 0,
    scans: 0,
    pendingSync: 0,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Conexión restaurada");
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("Modo offline activado");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      if (isOnline) {
        const response = await axios.get(`${API_BASE}/sync/status`, getAuthHeaders());
        setSyncStatus(response.data);
      }
    } catch (error) {
      // Silently handle sync status errors
    }
  }, [isOnline]);

  const countOfflineData = useCallback(async () => {
    try {
      const systems = await getFromStore("systems");
      const events = await getFromStore("events");
      const scans = await getFromStore("scans");
      const pending = await getFromStore("pending_sync");
      
      setOfflineData({
        systems: systems.length,
        events: events.length,
        scans: scans.length,
        pendingSync: pending.length,
      });
    } catch (error) {
      // Silently handle offline data count errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSyncStatus();
    countOfflineData();
  }, [fetchSyncStatus, countOfflineData]);

  const downloadAllData = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_BASE}/sync/download`, getAuthHeaders());
      const data = response.data;
      
      // Save to IndexedDB
      await saveToStore("systems", data.systems);
      await saveToStore("events", data.events);
      await saveToStore("scans", data.scans);
      
      // Save metadata
      const db = await openDB();
      const tx = db.transaction("metadata", "readwrite");
      tx.objectStore("metadata").put({
        key: "last_download",
        value: data.downloaded_at,
        chile_frequencies: data.chile_frequencies,
      });
      
      toast.success("Datos descargados para uso offline");
      countOfflineData();
    } catch (error) {
      toast.error("Error al descargar datos");
    } finally {
      setDownloading(false);
    }
  };

  const syncPendingData = async () => {
    setSyncing(true);
    try {
      const pendingItems = await getFromStore("pending_sync");
      
      if (pendingItems.length === 0) {
        toast.info("No hay datos pendientes de sincronización");
        setSyncing(false);
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      for (const item of pendingItems) {
        try {
          await axios.post(`${API_BASE}/sync/upload`, item, getAuthHeaders());
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        await clearStore("pending_sync");
        toast.success(`${successCount} elementos sincronizados`);
      }
      
      if (errorCount > 0) {
        toast.warning(`${errorCount} elementos con errores`);
      }
      
      countOfflineData();
      fetchSyncStatus();
    } catch (error) {
      toast.error("Error durante la sincronización");
    } finally {
      setSyncing(false);
    }
  };

  const clearOfflineData = async () => {
    try {
      await clearStore("systems");
      await clearStore("events");
      await clearStore("scans");
      await clearStore("pending_sync");
      toast.success("Datos offline eliminados");
      countOfflineData();
    } catch (error) {
      toast.error("Error al limpiar datos");
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Sincronización Offline
          </h1>
          <p className="text-sm text-[#71717A]">
            Trabaja sin conexión y sincroniza cuando estés online
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm ${
          isOnline ? "bg-[#34C759]/20 text-[#34C759]" : "bg-[#FFCC00]/20 text-[#FFCC00]"
        }`}>
          {isOnline ? <Cloud className="w-4 h-4" /> : <CloudOff className="w-4 h-4" />}
          <span className="text-sm font-medium">{isOnline ? "Online" : "Offline"}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Offline Data Status */}
        <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
          <h2 className="font-heading font-semibold text-[#F4F4F5] mb-6">
            Datos Almacenados Localmente
          </h2>
          
          {loading ? (
            <p className="text-[#71717A]">Cargando...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#0A0A0A] border border-white/[0.08] rounded-sm">
                  <p className="text-xs text-[#71717A] mb-1">Sistemas</p>
                  <p className="font-mono text-2xl text-[#F4F4F5]">{offlineData.systems}</p>
                </div>
                <div className="p-4 bg-[#0A0A0A] border border-white/[0.08] rounded-sm">
                  <p className="text-xs text-[#71717A] mb-1">Eventos</p>
                  <p className="font-mono text-2xl text-[#F4F4F5]">{offlineData.events}</p>
                </div>
                <div className="p-4 bg-[#0A0A0A] border border-white/[0.08] rounded-sm">
                  <p className="text-xs text-[#71717A] mb-1">Escaneos</p>
                  <p className="font-mono text-2xl text-[#F4F4F5]">{offlineData.scans}</p>
                </div>
                <div className={`p-4 border rounded-sm ${
                  offlineData.pendingSync > 0 
                    ? "bg-[#FFCC00]/10 border-[#FFCC00]/30" 
                    : "bg-[#0A0A0A] border-white/[0.08]"
                }`}>
                  <p className="text-xs text-[#71717A] mb-1">Pendiente Sync</p>
                  <p className={`font-mono text-2xl ${
                    offlineData.pendingSync > 0 ? "text-[#FFCC00]" : "text-[#F4F4F5]"
                  }`}>{offlineData.pendingSync}</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={downloadAllData}
                  disabled={downloading || !isOnline}
                  className="flex-1 btn-tactical btn-tactical-primary"
                  data-testid="download-offline-button"
                >
                  {downloading ? (
                    "Descargando..."
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Datos
                    </>
                  )}
                </Button>
                <Button
                  onClick={clearOfflineData}
                  variant="ghost"
                  className="btn-tactical btn-tactical-secondary"
                  data-testid="clear-offline-button"
                >
                  Limpiar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Sync Status */}
        <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
          <h2 className="font-heading font-semibold text-[#F4F4F5] mb-6">
            Estado de Sincronización
          </h2>
          
          <div className="space-y-4">
            {syncStatus?.last_sync ? (
              <div className="p-4 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-[#34C759]" />
                  <span className="font-medium text-[#34C759]">Última Sincronización</span>
                </div>
                <p className="text-sm text-[#A1A1AA]">
                  {new Date(syncStatus.last_sync.synced_at).toLocaleString()}
                </p>
                <p className="text-xs text-[#71717A] mt-1">
                  {syncStatus.last_sync.processed} elementos procesados
                </p>
              </div>
            ) : (
              <div className="p-4 bg-[#71717A]/10 border border-white/[0.08] rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-[#71717A]" />
                  <span className="font-medium text-[#71717A]">Sin Sincronización</span>
                </div>
                <p className="text-sm text-[#71717A]">
                  Descarga datos para trabajar offline
                </p>
              </div>
            )}

            {offlineData.pendingSync > 0 && (
              <div className="p-4 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Upload className="w-4 h-4 text-[#FFCC00]" />
                  <span className="font-medium text-[#FFCC00]">Datos Pendientes</span>
                </div>
                <p className="text-sm text-[#A1A1AA]">
                  {offlineData.pendingSync} elementos esperando sincronización
                </p>
              </div>
            )}

            <Button
              onClick={syncPendingData}
              disabled={syncing || !isOnline || offlineData.pendingSync === 0}
              className="w-full btn-tactical btn-tactical-primary"
              data-testid="sync-button"
            >
              {syncing ? (
                "Sincronizando..."
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sincronizar Ahora
                </>
              )}
            </Button>

            <div className="pt-4 border-t border-white/[0.08]">
              <h3 className="font-medium text-[#F4F4F5] mb-3">Cómo funciona</h3>
              <ul className="space-y-2 text-sm text-[#71717A]">
                <li className="flex items-start gap-2">
                  <span className="text-[#34C759]">1.</span>
                  Descarga datos cuando tengas conexión
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#34C759]">2.</span>
                  Trabaja normalmente sin internet
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#34C759]">3.</span>
                  Los cambios se guardan localmente
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#34C759]">4.</span>
                  Sincroniza cuando vuelvas a estar online
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
